import { createTheme, type ThemeOptions } from '@mui/material/styles';

// ─── Backward-compatible color tokens ────────────────────────────────────────
// These map to the CSS custom properties defined in styles/tokens.css.
// Components that previously imported `colors` will continue to work.
export const colors = {
  background: {
    primary: 'var(--bg-0)',
    secondary: 'var(--bg-1)',
    tertiary: 'var(--bg-2)',
  },
  border: 'var(--line)',
  text: {
    primary: 'var(--fg-0)',
    secondary: 'var(--fg-2)',
  },
  accent: {
    blue: 'var(--accent)',
    green: 'var(--ok)',
    orange: 'var(--warn)',
    red: 'var(--crit)',
    purple: 'var(--info)',
  },
  chart: [
    'var(--accent)',
    'var(--ok)',
    'var(--warn)',
    'var(--crit)',
    'var(--info)',
    'oklch(74% 0.155 270 / .7)',
    'oklch(76% 0.13 158 / .7)',
    'oklch(80% 0.14 80 / .7)',
  ],
};

// Typography constants
export const fontFamily = {
  sans: 'var(--font-sans)',
  mono: 'var(--font-mono)',
};

// ─── MUI Theme ───────────────────────────────────────────────────────────────

const themeOptions: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1024,
      xl: 1440,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#8b7cf6',
    },
    secondary: {
      main: '#6ea1f7',
    },
    success: {
      main: '#5ec269',
    },
    warning: {
      main: '#c9a84c',
    },
    error: {
      main: '#e05a52',
    },
    background: {
      default: '#1a1b23',
      paper: '#22232d',
    },
    text: {
      primary: '#f0f1f5',
      secondary: '#9a9bab',
    },
    divider: '#3a3b4a',
    action: {
      hover: 'rgba(139, 124, 246, 0.08)',
      selected: 'rgba(139, 124, 246, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.08)',
    },
  },
  typography: {
    fontFamily: 'var(--font-sans)',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      letterSpacing: '-0.025em',
      '@media (min-width:768px)': { fontSize: '3rem' },
      '@media (min-width:1024px)': { fontSize: '3.5rem' },
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      letterSpacing: '-0.02em',
      '@media (min-width:768px)': { fontSize: '2.5rem' },
      '@media (min-width:1024px)': { fontSize: '3rem' },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.015em',
      '@media (min-width:768px)': { fontSize: '1.75rem' },
      '@media (min-width:1024px)': { fontSize: '2.25rem' },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.01em',
      '@media (min-width:768px)': { fontSize: '1.5rem' },
      '@media (min-width:1024px)': { fontSize: '2rem' },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      '@media (min-width:768px)': { fontSize: '1.25rem' },
      '@media (min-width:1024px)': { fontSize: '1.5rem' },
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      '@media (min-width:768px)': { fontSize: '1.1rem' },
      '@media (min-width:1024px)': { fontSize: '1.25rem' },
    },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 400, color: 'var(--fg-2)' },
    caption: { color: 'var(--fg-3)' },
    overline: { color: 'var(--fg-3)', letterSpacing: '0.08em' },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 'var(--radius-sm)',
          padding: '8px 16px',
          transition: 'all 0.2s ease',
          '&.btn-primary': {
            backgroundColor: 'var(--accent)',
            color: 'oklch(15% 0.02 270)',
            borderColor: 'var(--accent)',
          },
        },
        contained: {
          boxShadow: 'var(--shadow-1)',
          '&:hover': {
            boxShadow: 'var(--shadow-2)',
          },
        },
        outlined: {
          borderColor: 'var(--line)',
          '&:hover': {
            borderColor: 'var(--accent-line)',
            backgroundColor: 'var(--accent-soft)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg-1)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-1)',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: 'var(--accent-line)',
            boxShadow: 'var(--shadow-2)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid var(--line-soft)',
          padding: '16px 20px',
        },
        title: {
          fontWeight: 600,
          fontSize: '1rem',
        },
        subheader: {
          color: 'var(--fg-2)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 'var(--pad)',
          '&:last-child': {
            paddingBottom: 'var(--pad)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg-1)',
          backgroundImage: 'none',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
        },
        elevation0: {
          border: 'none',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-1)',
          backgroundImage: 'none',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-sm)',
          boxShadow: 'var(--shadow-2)',
          marginTop: 4,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&:hover': {
            backgroundColor: 'var(--accent-soft)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--accent-soft)',
            '&:hover': {
              backgroundColor: 'var(--accent-soft)',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--glass-2)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid var(--line)',
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-1)',
          borderRight: '1px solid var(--line)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'var(--fg-2)',
          '&:hover': {
            color: 'var(--fg-0)',
            backgroundColor: 'var(--accent-soft)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: 'var(--fg-2)',
            fontWeight: 500,
            fontSize: '0.875rem',
            '&.Mui-focused': {
              color: 'var(--accent)',
            },
            '&.MuiInputLabel-shrink': {
              color: 'var(--fg-2)',
              fontWeight: 500,
            },
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--bg-2)',
            borderRadius: 'var(--radius-sm)',
            '& fieldset': {
              borderColor: 'var(--line)',
            },
            '&:hover fieldset': {
              borderColor: 'var(--accent-line)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'var(--accent)',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg-2)',
          borderRadius: 'var(--radius-sm)',
          '& fieldset': {
            borderColor: 'var(--line)',
          },
          '&:hover fieldset': {
            borderColor: 'var(--accent-line) !important',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'var(--accent) !important',
            borderWidth: '1px',
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            fontSize: '0.72em',
          },
        },
        input: {
          color: 'var(--fg-0)',
          '&::placeholder': {
            color: 'var(--fg-3)',
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--fg-2)',
          fontWeight: 500,
          fontSize: '0.875rem',
          '&.Mui-focused': {
            color: 'var(--accent)',
          },
          '&.MuiInputLabel-shrink': {
            color: 'var(--fg-2)',
            fontWeight: 500,
            backgroundColor: 'var(--bg-2)',
            padding: '0 6px',
            marginLeft: '-4px',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg-2)',
          color: 'var(--fg-0)',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--line)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--accent-line)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-sm)',
          fontWeight: 500,
        },
        outlined: {
          borderColor: 'var(--line)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: 'var(--fg-2)',
          '&.Mui-selected': {
            color: 'var(--accent)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'var(--accent)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid var(--line-soft)',
        },
        head: {
          fontWeight: 600,
          color: 'var(--fg-2)',
          backgroundColor: 'var(--bg-2)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'var(--accent-soft)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-1)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-2)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderBottom: '1px solid var(--line-soft)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--bg-2)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--fg-0)',
          fontSize: '0.75rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius)',
          border: '1px solid var(--line)',
        },
        standardSuccess: {
          backgroundColor: 'var(--ok-soft)',
          borderColor: 'var(--ok)',
        },
        standardWarning: {
          backgroundColor: 'var(--warn-soft)',
          borderColor: 'var(--warn)',
        },
        standardError: {
          backgroundColor: 'var(--crit-soft)',
          borderColor: 'var(--crit)',
        },
        standardInfo: {
          backgroundColor: 'var(--info-soft)',
          borderColor: 'var(--info)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: 'var(--bg-2)',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg-2)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'var(--line-soft)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-sm)',
          transition: 'background-color 0.2s ease, color 0.2s ease',
          '&:hover': {
            backgroundColor: 'var(--accent-soft)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--accent-soft)',
            borderLeft: '2px solid var(--accent)',
            '&:hover': {
              backgroundColor: 'var(--accent-soft)',
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: 'var(--accent)',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: 'var(--accent)',
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorError: {
          backgroundColor: 'var(--crit)',
        },
        colorSuccess: {
          backgroundColor: 'var(--ok)',
        },
      },
    },
  },
};

const theme = createTheme(themeOptions);

export default theme;

/**
 * Creates a MUI theme for the given mode (dark/light).
 * Call this reactively when theme changes.
 */
export function createAppTheme(mode: 'dark' | 'light') {
  const isDark = mode === 'dark';
  return createTheme({
    ...themeOptions,
    palette: {
      mode,
      primary: { main: isDark ? '#8b7cf6' : '#4361ee' },
      secondary: { main: isDark ? '#6ea1f7' : '#0d6efd' },
      success: { main: isDark ? '#5ec269' : '#198754' },
      warning: { main: isDark ? '#c9a84c' : '#e67700' },
      error: { main: isDark ? '#e05a52' : '#dc3545' },
      background: {
        default: isDark ? '#1a1b23' : '#ffffff',
        paper: isDark ? '#22232d' : '#ffffff',
      },
      text: {
        primary: isDark ? '#f0f1f5' : '#1a1a2e',
        secondary: isDark ? '#9a9bab' : '#495057',
      },
      divider: isDark ? '#3a3b4a' : '#dee2e6',
      action: {
        hover: isDark ? 'rgba(139, 124, 246, 0.08)' : 'rgba(67, 97, 238, 0.06)',
        selected: isDark ? 'rgba(139, 124, 246, 0.12)' : 'rgba(67, 97, 238, 0.10)',
        disabled: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
        disabledBackground: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
      },
    },
  });
}
